import {
  B2bAdminRoles,
  B2bAdminStatus,
  B2bAdminsLanguagePreference,
} from '@Common/types/bo/b2b/admins';
import {
  B2bCompanyFlag,
  KybStatus,
  PaymentMethodIntent,
  PaymentMethodPaymentType,
  PaymentMethodStatus,
  PaymentMethodType,
} from '@Common/types/bo/b2b/companies';
import { EmployeeStatus } from '@Common/types/bo/b2b/employees_properties';
import { MdlrBankNameFailedTaskRetryReason } from '@Common/types/bo/tasks/mdlr_bank_name_failed';

import { b2bCompaniesSavingPlansTranslations } from '@sh4res/bo-common/i18n/fr/b2b/companies_saving_plans';

export const b2bCompaniesTranslations = {
  choices: {
    kyb_status: {
      created: 'Créé',
      in_review: 'En cours de vérification',
      verified: 'KYB Complété',
      denied: 'Refusé',
      resigned: 'Démissionné',
    } satisfies Record<KybStatus, string>,
    flags: {
      vip: 'VIP',
    } satisfies Record<B2bCompanyFlag, string>,
    legal_form: {
      other: 'Autre',
    },
  },
  actions: {
    edit: {
      title: "Éditer l'entreprise",
      success: 'Entreprise mise à jour',
      failure: "Échec lors de la mise à jour de l'entreprise: %{error}",
      steps: {
        '1': "Informations sur l'entreprise",
        '2': 'Représentant légal',
        '3': 'Informations sur le distributeur',
        '4': 'Requête',
      },
    },
  },
  dashboard: {
    active_campaigns: {
      title: {
        singular: 'Opération active',
        plural: 'Opérations actives',
      },
      operation_id: 'ID opération',
      status: 'Statut',
      amount: 'Montant',
      in_progress: 'En cours',
      answer_rate: 'Taux de réponse',
      of_affiliates_answered: 'des affiliés ont répondu',
      no_active_campaigns: 'Aucune campagne active',
    },
  },
  sections: {
    company_info: {
      company: 'Entreprise',
      logo: "Logo de l'entreprise",
      details: "Détails de l'entreprise",
      address: "Adresse de l'entreprise",
      mailing_address: 'Adresse Postale',
      billing_address: 'Adresse de facturation',
      search: "Entrez le nom de l'entreprise ou le numéro Siret",
    },
    legal_representative: {
      details: 'Détails des représentants légaux de l’entreprise',
      add: 'Ajouter un représentant légal de l’entreprise',
      business_correspondent: {
        details: 'Détails des correspondants entreprise',
        add: 'Ajouter un correspondant entreprise',
      },
      fields: {
        role: "Fonction dans l'entreprise",
        firstname: 'Prénom',
        lastname: 'Nom',
        email: 'Email',
        error_email_domains:
          "L'adresse email ne correspond pas à au moins un des domaines de l'entreprise : %{domains}.",
        landline: 'Téléphone fixe',
        phone_number: 'Téléphone mobile',
        is_business_correspondent: 'Est correspondant entreprise',
      },
    },
  },
  employee_status: {
    current: 'Présent',
    past: 'Sorti',
    retired: 'Retraité',
  } satisfies Record<EmployeeStatus, string>,
  saving_plans: b2bCompaniesSavingPlansTranslations,
  documents: {
    button: 'Déposer un document',
    empty: "Cette entreprise n'a aucun document",
    create: {
      title: "Déposer un document d'entreprise",
      success: 'Document téléchargé',
      failure: 'Erreur lors du téléchargement du document : %{error}',
      label: 'Télécharger Document',
    },
  },
  payment_methods: {
    button: 'Ajouter un moyen de paiement',
    empty: "Cette entreprise n'a pas encore de méthodes de paiement",
    create: {
      title: 'Ajouter un moyen de paiement',
      success: 'Méthode de paiement ajoutée',
      failure: "Erreur lors de l'ajout de la méthode de paiement : %{error}",
      label: 'Ajouter un moyen de paiement',
    },
    validation: {
      scan_sort_code_invalid: 'Format de code guichet britannique invalide',
      scan_account_number_invalid: 'Format de numéro de compte britannique invalide',
      intl_account_number_is_iban:
        'Cette valeur ressemble à un IBAN. Veuillez sélectionner le type de compte IBAN.',
      intl_sepa_zone_not_allowed:
        'Les banques de la zone SEPA doivent utiliser le type de compte IBAN.',
    },
    delete_confirm: {
      title: 'Supprimer le moyen de paiement',
      content:
        'Êtes-vous sûr de vouloir supprimer ce moyen de paiement ? Cette action est irréversible.',
      shared_account_info:
        'Ce moyen de paiement partage le même mandat / compte bancaire avec un autre moyen de paiement. Seul ce moyen de paiement sera supprimé, le mandat / compte bancaire reste actif pour l’autre.',
    },
    update: {
      title: 'Mettre à jour',
      reason: 'Raison du rejet temporaire',
      documents: 'Documents',
      subtitle: 'Veuillez fournir le document manquant requis pour finaliser votre demande',
      footnote:
        'Vos paiements par carte bancaire ou par prélèvement SEPA sont traités en toute sécurité par Checkout SAS, établissement de monnaie électronique et prestataire de service de paiement, agréé et régulé par l’ACPR sous le numéro 17208, conformément à ses conditions générales et à la réglementation en vigueur.',
      failure: 'Erreur lors de la modification de la méthode de paiement : %{error}',
    },
    title: 'Moyen de paiement',
    sections: {
      empty: {
        title: 'Moyen de paiement',
        no_payment_methods: "Vous n'avez pas encore configuré de moyen de paiement.",
      },
      payment_method: {
        title: 'Moyens de paiement',
        intent:
          'Le moyen de paiement choisi sera utilisé pour le règlement de vos opérations financières',
        payment_method_type: 'Choix du moyen de paiement',
        payment_method_intent: 'Finalité du moyen de paiement',
      },
      details: {
        title: 'Détails',
        sepa_direct_debit: {
          title: 'Détails',
          details: 'Compléter vos coordonnées bancaires',
          description:
            'Ce prélèvement SEPA sera utilisé pour le règlement de vos opérations financières.',
          invoicing_details:
            'Ajoutez des détails pour configurer le prélèvement SEPA pour la facturation',
          ccb_withdrawal_description: 'Ce prélèvement SEPA sera utilisé pour les retraits CCB.',
          payment_type: 'Type de paiement*',
          bic: 'BIC / SWIFT',
          iban: 'IBAN',
          rib: {
            title: 'RIB',
            upload: 'Importer le RIB associé au nom de %{company_name}',
          },
          add_second_payment_method: 'Ajoutez un autre prélèvement SEPA pour la facturation',
        },
        external_bank: {
          title: 'Détails',
          details: 'Ajoutez des détails pour lier un compte bancaire',
          description:
            'Ce compte bancaire sera utilisé comme moyen principal de paiement pour le recouvrement de tous les paiements liés à votre contrat (opérations collectives, abondements...)',
          invoicing_details: 'Ajoutez des détails pour lier un compte bancaire pour la facturation',
          ccb_withdrawal_description: 'Ce compte bancaire sera utilisé pour les retraits CCB.',
          payment_type: 'Type de paiement*',
          bic: 'BIC / SWIFT',
          iban: 'IBAN',
          account_type: 'Type de compte',
          account_type_iban: 'Compte IBAN',
          account_type_intl: 'Compte international',
          account_type_scan: 'Compte SCAN (UK)',
          account_number: 'Numéro de compte',
          sort_code: 'Code guichet',
          rib: {
            title: 'RIB',
            upload:
              'Téléchargez un RIB pour le compte spécifié ci-dessus au nom de %{company_name}',
          },
        },
        checkout_disclaimer:
          'Vos paiements par carte bancaire ou par prélèvement SEPA sont traités en toute sécurité par Checkout SAS, établissement de monnaie électronique et prestataire de service de paiement, agréé et régulé par l’ACPR sous le numéro 17208, conformément à ses conditions générales et à la réglementation en vigueur.',
        signature_authorization: {
          title: 'Signature',
          question: 'Avez-vous l’autorisation de signer le mandat de prélèvement SEPA ?',
          options: {
            self: 'Oui, j’ai le droit',
            external_signatory: 'Non, je délègue la signature à une personne autorisée',
          },
          hint: 'Si vous n’avez pas l’autorisation, nous vous demanderons à l’étape suivante de renseigner la personne habilitée à signer.',
        },
      },
      signature: {
        title: 'Signature',
        billing_title: 'Détails',
        iban: 'IBAN',
        bic: 'BIC / SWIFT',
        acknowledgements_title: 'Signature',
        subheader: 'Mandat de prélèvement SEPA',
        sepa_mandate_body_start: 'En signant le',
        sepa_mandate_body_end:
          ", j'autorise AXA Epargne Entreprise, via son prestataire Checkout SAS, à débiter le compte bancaire de mon entreprise.",
        electronic_signature_note:
          'En enregistrant cette opération, je reconnais signer électroniquement mon mandat.',
        sepa_mandate_link: {
          loading: 'Génération du document...',
          generate_failure: 'Échec de la génération du document',
          fetch_failure: 'Impossible de charger le document',
        },
        external_signatory: {
          intro:
            'Renseignez les informations de la personne autorisée à signer le mandat afin qu’un lien de signature lui soit envoyé pour finaliser la demande.',
          firstname: 'Prénom',
          lastname: 'Nom',
          email: 'Adresse e-mail',
        },
      },
      success_modal: {
        sepa_direct_debit: {
          title: 'Demande enregistrée',
          body: 'Il faudra quelques jours pour traiter votre demande. Nous vous tiendrons informé.',
          btn: 'Compris',
        },
        sepa_direct_debit_external_signatory: {
          title: 'Demande enregistrée',
          body: 'Un e-mail contenant les documents à signer a été envoyé au destinataire que vous venez de renseigner. Nous vous tiendrons informé.',
          btn: 'Compris',
        },
        external_bank: {
          title: 'Compte bancaire lié',
          body: `Félicitations ! Votre compte bancaire a été lié avec succès. Il doit maintenant être vérifié avant d'être effectif. Vous pouvez suivre son statut dans l'onglet Informations de paiement. `,
          btn: 'Compris',
        },
      },
    },
    items: {
      sepa_direct_debit: {
        principal: {
          title: 'Prélèvement SEPA - Opérations financières',
        },
        invoicing: {
          title: 'Prélèvement SEPA - Facturation',
        },
        ccb_withdrawal: {
          title: 'Prélèvement SEPA - Retraits CCB',
        },
      },
      external_bank: {
        principal: {
          title: 'Compte bancaire - Opérations financières',
        },
        invoicing: {
          title: 'Compte bancaire lié - Facturation',
        },
        ccb_withdrawal: {
          title: 'Compte bancaire - Retraits CCB',
        },
      },
    },
    choices: {
      payment_types: {
        recurring: 'Récurrent',
        one_off: 'Ponctuel',
      } satisfies Record<PaymentMethodPaymentType, string>,
      statuses: {
        active: 'Actif',
        deleted: 'Supprimé',
        pending: "En cours d'examen",
        rejected: 'Rejeté',
        awaiting_customer: 'En attente du client',
        awaiting_signature: 'En attente de la signature externe',
      } satisfies Record<PaymentMethodStatus, string>,
      payment_method_type: {
        sepa_direct_debit: 'Configurer un prélèvement SEPA',
        external_bank: 'Associer un compte bancaire pour les paiements par virement',
      } satisfies Record<PaymentMethodType, string>,
      intents: {
        principal: 'Opérations financières',
        invoicing: 'Facturation',
        ccb_withdrawal: 'Retraits CCB',
      } satisfies Record<PaymentMethodIntent, string>,
      retry_reasons: {
        RETRY_INCOMPLETE_DOCUMENT: 'Réessayer - Document incomplet',
        RETRY_DOCUMENT_NOT_SUPPORTED: 'Réessayer - Document non pris en charge',
        RETRY_DOCUMENT_OLD_DOCUMENT: 'Réessayer - Document ancien',
        RETRY_POI_EXPIRED: 'Réessayer - POI expiré',
        RETRY_POI_INCOMPLETE: 'Réessayer - POI incomplet',
        RETRY_POI_NOT_SUPPORTED: 'Réessayer - POI non pris en charge',
        RETRY_POI_AND_RIB_CANNOT_BE_ACCEPTED:
          'Réessayer - Le POI et le RIB ne peuvent pas être acceptés',
        RETRY_RIB_INCOMPLETE: 'Réessayer - RIB incomplet',
        RETRY_NOT_SUPPORTED_DOCUMENT_NOT_RIB:
          'Réessayer - Document non pris en charge (pas un RIB)',
        RETRY_POOR_IMAGE_QUALITY: 'Réessayer - Mauvaise qualité d’image',
        RETRY_RANDOM_UPLOAD: 'Réessayer - Document non conforme',
        RETRY_OTHER: 'Réessayer',
      } satisfies Record<MdlrBankNameFailedTaskRetryReason, string>,
    },
    fields: {
      status: 'Statut',
      retry_comment: 'Motif du rejet',
      bank_name: 'Nom de la banque',
      iban: 'Numéro de compte bancaire',
      beneficiary_name: 'Nom du bénéficiaire',
      created_at: 'Créé le',
      updated_at: 'Modifié le',
      payment_type: 'Type de paiement',
      mandate_unique_reference: 'Référence unique du mandat',
      mandate_singed_at: 'Date de signature',
      mandate_signature_document_url: 'Document du mandat',
    },
    actions: {
      create: {
        failure: 'Erreur lors de la création de la méthode de paiement : %{error}',
      },
    },
  },
  admins: {
    choices: {
      role: {
        admin_level_1: 'Niveau 1 - Consultation basique',
        admin_level_2: 'Niveau 2 - Consultation avancée',
        admin_level_3: 'Niveau 3 - Consultation et opérations collectives uniquement',
        admin_level_4: 'Niveau 4 - Administrateur',
      } satisfies Record<B2bAdminRoles, string>,
      language_preference: {
        FR: 'Français',
        EN: 'Anglais',
      } satisfies Record<B2bAdminsLanguagePreference, string>,
      status: {
        active: 'Actif',
        inactive: 'Inactif',
        pending: 'En attente',
        invited: 'Invité',
      } satisfies Record<B2bAdminStatus, string>,
    },
  },
};
